import React, { useState, useCallback } from 'react';
import { Card, Form, Select, DatePicker, Input, Space, Button, Row, Col } from 'antd';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Search } = Input;

const UnifiedFilters = ({ onFiltersChange, loading = false }) => {
    const [filters, setFilters] = useState({
        timeRange: '5m',
        status: 'all',
        dateRange: null,
        latencyMin: null,
        latencyMax: null,
        hostFilter: ''
    });

    const handleFilterChange = useCallback((changedFilters, allFilters) => {
        const newFilters = { ...filters, ...allFilters };

        // Clear date range when not using custom time range
        if (changedFilters.timeRange && changedFilters.timeRange !== 'custom') {
            newFilters.dateRange = null;
        }

        setFilters(newFilters);

        // Call parent callback with formatted filters
        if (onFiltersChange) {
            const formattedFilters = {
                timeRange: newFilters.timeRange,
                status: newFilters.status,
                latencyMin: newFilters.latencyMin,
                latencyMax: newFilters.latencyMax,
                hostFilter: newFilters.hostFilter
            };

            // Add custom date range if selected
            if (newFilters.timeRange === 'custom' && newFilters.dateRange) {
                formattedFilters.startDate = newFilters.dateRange[0].toISOString();
                formattedFilters.endDate = newFilters.dateRange[1].toISOString();
            }

            onFiltersChange(formattedFilters);
        }
    }, [filters, onFiltersChange]);

    const resetFilters = useCallback(() => {
        const defaultFilters = {
            timeRange: '5m',
            status: 'all',
            dateRange: null,
            latencyMin: null,
            latencyMax: null,
            hostFilter: ''
        };
        setFilters(defaultFilters);

        if (onFiltersChange) {
            onFiltersChange({
                timeRange: '5m',
                status: 'all',
                latencyMin: null,
                latencyMax: null,
                hostFilter: ''
            });
        }
    }, [onFiltersChange]);

    return (
        <Card
            size="small"
            title={
                <Space>
                    <FilterOutlined />
                    Filters
                </Space>
            }
            extra={
                <Button
                    icon={<ReloadOutlined />}
                    onClick={resetFilters}
                    size="small"
                >
                    Reset
                </Button>
            }
            style={{ marginBottom: 16 }}
        >
            <Form
                layout="vertical"
                initialValues={filters}
                onValuesChange={handleFilterChange}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="Time Range"
                            name="timeRange"
                        >
                            <Select style={{ width: '100%' }} disabled={loading}>
                                <Option value="5m">Last 5 Minutes</Option>
                                <Option value="15m">Last 15 Minutes</Option>
                                <Option value="30m">Last 30 Minutes</Option>
                                <Option value="45m">Last 45 Minutes</Option>
                                <Option value="1h">Last 1 Hour</Option>
                                <Option value="6h">Last 6 Hours</Option>
                                <Option value="24h">Last 24 Hours</Option>
                                <Option value="7d">Last 7 Days</Option>
                                <Option value="30d">Last 30 Days</Option>
                                <Option value="custom">Custom Range</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.timeRange !== currentValues.timeRange}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('timeRange') === 'custom' ? (
                                    <Form.Item
                                        label="Date Range"
                                        name="dateRange"
                                    >
                                        <RangePicker
                                            showTime
                                            format="YYYY-MM-DD HH:mm:ss"
                                            style={{ width: '100%' }}
                                            disabled={loading}
                                        />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="Status"
                            name="status"
                        >
                            <Select style={{ width: '100%' }} disabled={loading}>
                                <Option value="all">All Status</Option>
                                <Option value="UP">UP Only</Option>
                                <Option value="DOWN">DOWN Only</Option>
                                <Option value="TIMEOUT">Timeout Only</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="Host Filter"
                            name="hostFilter"
                        >
                            <Search
                                placeholder="Filter by host..."
                                allowClear
                                disabled={loading}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Latency Range (ms)">
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item noStyle name="latencyMin">
                                    <Input
                                        style={{ width: '45%' }}
                                        placeholder="Min"
                                        type="number"
                                        disabled={loading}
                                    />
                                </Form.Item>
                                <Input
                                    style={{
                                        width: '10%',
                                        textAlign: 'center',
                                        borderLeft: 0,
                                        borderRight: 0,
                                        pointerEvents: 'none',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                    placeholder="~"
                                    disabled
                                />
                                <Form.Item noStyle name="latencyMax">
                                    <Input
                                        style={{ width: '45%' }}
                                        placeholder="Max"
                                        type="number"
                                        disabled={loading}
                                    />
                                </Form.Item>
                            </Space.Compact>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};

export default UnifiedFilters;
